export type StableHorse = {
  shareId: number;
  horseId: string;
  name: string;
  tier: string;
  archetype: string;
  track: string;
  trackName?: string;
  shareBp: number;
  source: string;
  acquiredAt: string;
  acquisitionCostCents: number;
  isRetired: boolean;
  nextRaceId?: string | null;
  nextRaceAt?: string | null;
  /** This player's paid purse+prize cents attributed to this horse. */
  earnedCents?: number;
  quoteKind?: "window" | "standing" | null;
  quoteCents?: number | null;
  appearance?: Record<string, unknown>;
  career: {
    races: number;
    wins: number;
    places?: number;
    shows?: number;
    points: number;
    purseEarningsCents: number;
    prizeEarningsCents?: number;
    form: string;
  };
};

export type StableResponse = { stable: StableHorse[] };

export type LedgerEntryView = {
  entryId: string;
  type: string;
  purpose: string;
  status: string;
  withdrawability: string;
  direction: "credit" | "debit";
  amountCents: number;
  currency: string;
  createdAt: string;
  completedAt: string | null;
  ref: Record<string, unknown>;
};

export type LedgerResponse = {
  entries: LedgerEntryView[];
  nextCursor: string | null;
};

/** Per-horse net: paid purse+prize shares minus what the share cost.
 * Display-only exact arithmetic over server integer cents. */
export function horseNetCents(horse: {
  earnedCents?: number;
  acquisitionCostCents: number;
}): number {
  return (horse.earnedCents ?? 0) - horse.acquisitionCostCents;
}

/** Plain-language labels for purpose-stamped ledger legs. */
export function ledgerEntryLabel(entry: {
  type: string;
  purpose: string;
}): string {
  switch (entry.purpose) {
    case "pack":
      return entry.type === "buyback" ? "Instant sell-back" : "Pack purchase";
    case "repack":
      return "Re-pack purchase";
    case "marketplace":
      if (entry.type === "sale") return "Marketplace sale";
      if (entry.type === "listing_fee") return "Listing fee";
      return "Marketplace purchase";
    case "buyback":
      return "Sold to stable";
    case "pack_rebate":
      return "Instant sell-back";
    case "purse":
      return "Purse payout";
    case "weekly_prize":
      return "Weekly prize";
    case "season_prize":
      return "Season championship";
    case "jackpot":
      return "Jackpot";
    case "platform_bet":
      if (entry.type === "bet_win") return "Bet won";
      if (entry.type === "bet_void") return "Bet refunded";
      return "Bet placed";
    default:
      return entry.purpose.replaceAll("_", " ");
  }
}
